import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Fighter, createFighter } from '../types/fighter';

export interface GameState {
  // Валюта и ресурсы
  gold: number;
  resources: {
    metal: number;
    wood: number;
    crystals: number;
    leather: number; // Кожа
  };

  // Прогресс
  liberation: number; // 0-100%
  currentBiome: 'plains' | 'forest' | 'mountains';

  // Постройки
  buildings: {
    barracks: boolean;
    forge: boolean;
    temple: boolean;
    mageTower: boolean;
  };

  // Бойцы
  fighters: Fighter[];
  maxFighters: number;

  // Инвентарь оружия (id -> количество)
  weaponInventory: Record<number, number>;

  // Инвентарь зелий
  potions: {
    greenPotion: number; // Зелёное зелье (медленное лечение между боями)
    redPotion: number; // Красное зелье (быстрое лечение в бою)
  };

  // Таймеры
  attackTimer: number; // секунды до нападения
  lastAttackTime: number; // timestamp последнего нападения
  lastRaidTime: number; // timestamp последнего рейда
  lastHPUpdate: number; // timestamp последнего обновления HP (регенерация)
  lastTaxCollectionTime: number; // timestamp последнего сбора налогов

  // Действия
  addGold: (amount: number) => void;
  spendGold: (amount: number) => boolean;
  addResource: (type: 'metal' | 'wood' | 'crystals' | 'leather', amount: number) => void;
  spendResource: (type: 'metal' | 'wood' | 'crystals' | 'leather', amount: number) => boolean;
  addLiberation: (amount: number) => void;
  buildBuilding: (building: keyof GameState['buildings']) => boolean;
  addFighter: (fighter: Fighter) => void;
  removeFighter: (fighterId: string) => void;
  updateFighter: (fighterId: string, updates: Partial<Fighter>) => void;
  regenerateFightersHP: () => void;

  // Инвентарь
  addWeaponToInventory: (weaponId: number, amount: number) => void;
  takeWeaponFromInventory: (weaponId: number, amount: number) => boolean;

  // Зелья
  addPotion: (type: 'greenPotion' | 'redPotion', amount: number) => void;
  usePotion: (type: 'greenPotion' | 'redPotion') => boolean;

  updateAttackTimer: (seconds: number) => void;
  resetAttackTimer: () => void;
  saveGame: () => void;
  loadGame: () => void;
  resetGame: () => void;
  hasSeenIntro: boolean;
  setHasSeenIntro: (seen: boolean) => void;
  hasSeenBaseTutorial: boolean;
  setHasSeenBaseTutorial: (seen: boolean) => void;
  hasSeenBattleTutorial: boolean;
  setHasSeenBattleTutorial: (seen: boolean) => void;
  extendedTutorialStep: number; // Текущий шаг расширенного туториала
  extendedTutorialPhase: 'resources' | 'taxHouse' | 'barracks' | 'raid' | 'battle' | null;
  setExtendedTutorialStep: (step: number) => void;
  setExtendedTutorialPhase: (phase: 'resources' | 'taxHouse' | 'barracks' | 'raid' | 'battle' | null) => void;
  setTestMode: () => void;
  skipAllTutorials: () => void;

  // Статистика рейдов (глубина -> количество успешных)
  raidStats: Record<number, number>;
  incrementRaidStat: (depth: number) => void;

  // Налоги
  collectTaxes: () => { success: boolean; amount: number; message?: string };
  getTaxAmount: () => number;
  getTaxCooldown: () => number; // Возвращает оставшееся время в секундах

  // Нападения орков
  generateBaseAttackEnemies: () => Fighter[]; // Генерирует врагов для нападения на базу
  getAttackCooldown: () => number; // Возвращает оставшееся время до нападения в секундах
  getAttackInfo: () => { count: number; composition: { young: number; fighter: number; veteran: number } }; // Информация о следующем нападении

  // Локализация
  language: 'ru' | 'en' | 'es' | 'fr' | 'de' | 'zh';
  setLanguage: (lang: 'ru' | 'en' | 'es' | 'fr' | 'de' | 'zh') => void;

  // Настройки музыки
  musicEnabled: boolean;
  musicVolume: number; // 0.0 - 1.0
  setMusicEnabled: (enabled: boolean) => void;
  setMusicVolume: (volume: number) => void;
}

const getAttackTimer = (liberation: number): number => {
  const wave = Math.floor(liberation / 10) + 1;
  return wave * 3600; // часы в секунды
};

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Начальные значения
      gold: 100,
      resources: {
        metal: 50,
        wood: 30,
        crystals: 10,
        leather: 10, // Начальное количество кожи
      },
      liberation: 0,
      currentBiome: 'plains',
      buildings: {
        barracks: false,
        forge: false,
        temple: false,
        mageTower: false,
      },
      fighters: [],
      maxFighters: 5,
      weaponInventory: {
        0: 0, // Дубина (клуб)
        1: 0, // Меч малый
        2: 0, // Меч большой
        3: 0, // Щит малый
        4: 0, // Щит большой
        5: 0, // Лук малый
        6: 0, // Лук большой
        7: 0, // Щит деревянный
        8: 0, // Кожаные доспехи
        9: 0, // Металлические доспехи
      },
      potions: {
        greenPotion: 0, // Зелёное зелье (медленное лечение между боями)
        redPotion: 0, // Красное зелье (быстрое лечение в бою)
      },
      attackTimer: 3600, // 1 час
      lastAttackTime: Date.now(),
      lastRaidTime: 0,
      lastHPUpdate: Date.now(),
      lastTaxCollectionTime: 0, // Время последнего сбора налогов
      language: 'en',
      setLanguage: (lang) => set({ language: lang }),
      musicEnabled: true,
      musicVolume: 0.5,
      setMusicEnabled: (enabled) => set({ musicEnabled: enabled }),
      setMusicVolume: (volume) => set({ musicVolume: Math.max(0, Math.min(1, volume)) }),
      hasSeenIntro: false,
      setHasSeenIntro: (seen) => set({ hasSeenIntro: seen }),
      hasSeenBaseTutorial: false,
      setHasSeenBaseTutorial: (seen) => {
        const state = get();
        set({ hasSeenBaseTutorial: seen });
        // После завершения базового туториала начинаем расширенный
        // Но только если обучение не было пропущено
        // Проверяем: если hasSeenBattleTutorial уже true ИЛИ extendedTutorialPhase === null с extendedTutorialStep === -1,
        // значит обучение было пропущено через skipAllTutorials
        if (seen && !state.hasSeenBattleTutorial && !(state.extendedTutorialPhase === null && state.extendedTutorialStep === -1)) {
          set({ extendedTutorialStep: 0, extendedTutorialPhase: 'resources' });
        }
      },
      hasSeenBattleTutorial: false,
      setHasSeenBattleTutorial: (seen) => set({ hasSeenBattleTutorial: seen }),
      extendedTutorialStep: -1,
      extendedTutorialPhase: null,
      setExtendedTutorialStep: (step) => set({ extendedTutorialStep: step }),
      setExtendedTutorialPhase: (phase) => set({ extendedTutorialPhase: phase }),
      setTestMode: () => set({
        gold: 500,
        resources: { metal: 500, wood: 500, crystals: 500, leather: 500 },
        hasSeenIntro: true,
        hasSeenBaseTutorial: true,
      }),
      skipAllTutorials: () => set({
        hasSeenBaseTutorial: true,
        hasSeenBattleTutorial: true,
        extendedTutorialPhase: null,
        extendedTutorialStep: -1,
      }),

      raidStats: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      incrementRaidStat: (depth) => set((state) => ({
        raidStats: {
          ...state.raidStats,
          [depth]: (state.raidStats[depth] || 0) + 1
        }
      })),

      // Налоги
      getTaxAmount: () => {
        const state = get();
        const liberation = state.liberation;
        if (liberation < 20) return 20;
        if (liberation < 40) return 40;
        if (liberation < 60) return 80;
        if (liberation < 80) return 120;
        return 200; // 80-100%
      },

      getTaxCooldown: () => {
        const state = get();
        const now = Date.now();
        const lastTax = state.lastTaxCollectionTime || 0;
        const cooldownMs = 15 * 60 * 1000; // 15 минут в миллисекундах
        const elapsed = now - lastTax;
        const remaining = Math.max(0, cooldownMs - elapsed);
        return Math.floor(remaining / 1000); // Возвращаем в секундах
      },

      collectTaxes: () => {
        const state = get();
        const cooldown = state.getTaxCooldown();

        if (cooldown > 0) {
          const minutes = Math.floor(cooldown / 60);
          const seconds = cooldown % 60;
          return {
            success: false,
            amount: 0,
            message: `Налоги можно собирать через ${minutes}:${seconds.toString().padStart(2, '0')}`
          };
        }

        const taxAmount = state.getTaxAmount();
        state.addGold(taxAmount);
        set({ lastTaxCollectionTime: Date.now() });

        return {
          success: true,
          amount: taxAmount
        };
      },

      // Нападения орков
      getAttackCooldown: () => {
        const state = get();
        const now = Date.now();
        const lastAttack = state.lastAttackTime || 0;
        const cooldownMs = 60 * 60 * 1000; // 1 час в миллисекундах
        const elapsed = now - lastAttack;
        const remaining = Math.max(0, cooldownMs - elapsed);
        return Math.floor(remaining / 1000); // Возвращаем в секундах
      },

      getAttackInfo: () => {
        const state = get();
        const liberation = state.liberation;

        // Определяем состав в зависимости от процента освобождения
        let young = 0, fighter = 0, veteran = 0;

        if (liberation < 10) {
          // 0-10%: 3 простых орка
          young = 3;
        } else if (liberation < 20) {
          // 10-20%: 4 орка (3 простых + 1 боец)
          young = 3;
          fighter = 1;
        } else if (liberation < 30) {
          // 20-30%: 5 орков (2 простых + 2 бойца + 1 ветеран)
          young = 2;
          fighter = 2;
          veteran = 1;
        } else if (liberation < 40) {
          // 30-40%: 6 орков (2 простых + 2 бойца + 2 ветерана)
          young = 2;
          fighter = 2;
          veteran = 2;
        } else if (liberation < 50) {
          // 40-50%: 7 орков (1 простой + 3 бойца + 3 ветерана)
          young = 1;
          fighter = 3;
          veteran = 3;
        } else if (liberation < 60) {
          // 50-60%: 8 орков (1 простой + 3 бойца + 4 ветерана)
          young = 1;
          fighter = 3;
          veteran = 4;
        } else if (liberation < 70) {
          // 60-70%: 9 орков (0 простых + 4 бойца + 5 ветеранов)
          fighter = 4;
          veteran = 5;
        } else if (liberation < 80) {
          // 70-80%: 10 орков (0 простых + 3 бойца + 7 ветеранов)
          fighter = 3;
          veteran = 7;
        } else if (liberation < 90) {
          // 80-90%: 10 орков (0 простых + 2 бойца + 8 ветеранов)
          fighter = 2;
          veteran = 8;
        } else {
          // 90-100%: 10 орков (0 простых + 1 боец + 9 ветеранов)
          fighter = 1;
          veteran = 9;
        }

        return {
          count: young + fighter + veteran,
          composition: { young, fighter, veteran }
        };
      },

      generateBaseAttackEnemies: () => {
        const state = get();
        const info = state.getAttackInfo();
        const enemies: Fighter[] = [];

        // Функции для создания типов врагов
        const createYoungOrc = (index: number): Fighter => {
          // Молодой орк: Кинжал (10) или Кинжал + Костяной нагрудник (13)
          const hasChest = Math.random() < 0.5;
          if (hasChest) {
            return createFighter(
              `attack-enemy-young-${index}`,
              'Молодой орк',
              0,
              0,
              false,
              10, // Костяной кинжал
              13  // Костяной нагрудник
            );
          } else {
            return createFighter(
              `attack-enemy-young-${index}`,
              'Молодой орк',
              0,
              0,
              false,
              10  // Костяной кинжал
            );
          }
        };

        const createFighterOrc = (index: number): Fighter => {
          // Орк-боец: различные комбинации ранга 1
          const rand = Math.random();
          if (rand < 0.33) {
            // Топор-щитовик: Топор (11) + Деревянный щит (14)
            return createFighter(
              `attack-enemy-fighter-${index}`,
              'Орк-боец',
              0,
              0,
              false,
              11, // Ржавый топор
              14  // Деревянный щит орка
            );
          } else if (rand < 0.66) {
            // Мутант-кинжальщик: Кинжал (10) + Кожа мутанта (18)
            return createFighter(
              `attack-enemy-fighter-${index}`,
              'Орк-боец',
              0,
              0,
              false,
              10, // Костяной кинжал
              undefined,
              18  // Кожа мутанта
            );
          } else {
            // Костяной топорщик: Топор (11) + Костяные доспехи (19)
            return createFighter(
              `attack-enemy-fighter-${index}`,
              'Орк-боец',
              0,
              0,
              false,
              11, // Ржавый топор
              undefined,
              19  // Костяные доспехи
            );
          }
        };

        const createVeteranOrc = (index: number): Fighter => {
          // Орк-ветеран: различные комбинации ранга 2
          const rand = Math.random();
          if (rand < 0.25) {
            // Каменный разрушитель: Булава (12) + Каменный щит (15)
            return createFighter(
              `attack-enemy-veteran-${index}`,
              'Орк-ветеран',
              0,
              0,
              false,
              12, // Каменная булава
              15  // Каменный щит
            );
          } else if (rand < 0.5) {
            // Копейщик: Орочье копьё (17)
            return createFighter(
              `attack-enemy-veteran-${index}`,
              'Орк-ветеран',
              0,
              0,
              false,
              17  // Орочье копьё
            );
          } else if (rand < 0.75) {
            // Метатель: Метательный томагавк (16)
            return createFighter(
              `attack-enemy-veteran-${index}`,
              'Орк-ветеран',
              0,
              0,
              false,
              16  // Метательный томагавк
            );
          } else {
            // Булава + Костяные доспехи: Булава (12) + Костяные доспехи (19)
            return createFighter(
              `attack-enemy-veteran-${index}`,
              'Орк-ветеран',
              0,
              0,
              false,
              12, // Каменная булава
              undefined,
              19  // Костяные доспехи
            );
          }
        };

        // Генерируем врагов
        for (let i = 0; i < info.composition.young; i++) {
          enemies.push(createYoungOrc(i));
        }
        for (let i = 0; i < info.composition.fighter; i++) {
          enemies.push(createFighterOrc(i));
        }
        for (let i = 0; i < info.composition.veteran; i++) {
          enemies.push(createVeteranOrc(i));
        }

        return enemies;
      },

      // Действия
      addGold: (amount) => set((state) => ({ gold: state.gold + amount })),

      spendGold: (amount) => {
        const state = get();
        if (state.gold >= amount) {
          set({ gold: state.gold - amount });
          return true;
        }
        return false;
      },

      addResource: (type, amount) =>
        set((state) => ({
          resources: {
            ...state.resources,
            [type]: (state.resources[type] || 0) + amount, // Защита от undefined/NaN
          },
        })),

      spendResource: (type, amount) => {
        const state = get();
        const currentAmount = state.resources[type] || 0; // Защита от undefined/NaN
        if (currentAmount >= amount) {
          set({
            resources: {
              ...state.resources,
              [type]: currentAmount - amount,
            },
          });
          return true;
        }
        return false;
      },

      addLiberation: (amount) =>
        set((state) => ({
          liberation: Math.max(0, Math.min(100, state.liberation + amount)),
        })),

      buildBuilding: (building) => {
        const state = get();
        if (state.buildings[building]) return false;

        // Проверка стоимости
        let canBuild = false;
        if (building === 'barracks') {
          canBuild = state.resources.metal >= 50;
          if (canBuild) {
            set({
              buildings: { ...state.buildings, barracks: true },
              resources: { ...state.resources, metal: state.resources.metal - 50 },
              maxFighters: 15,
            });
          }
        } else if (building === 'forge') {
          canBuild = state.resources.metal >= 50;
          if (canBuild) {
            set({
              buildings: { ...state.buildings, forge: true },
              resources: { ...state.resources, metal: state.resources.metal - 50 },
            });
          }
        } else if (building === 'temple') {
          canBuild = state.resources.wood >= 100;
          if (canBuild) {
            set({
              buildings: { ...state.buildings, temple: true },
              resources: { ...state.resources, wood: state.resources.wood - 100 },
            });
          }
        } else if (building === 'mageTower') {
          canBuild = state.resources.crystals >= 30;
          if (canBuild) {
            set({
              buildings: { ...state.buildings, mageTower: true },
              resources: { ...state.resources, crystals: state.resources.crystals - 30 },
            });
          }
        }

        return canBuild;
      },

      addFighter: (fighter) =>
        set((state) => {
          if (state.fighters.length >= state.maxFighters) return state;
          return { fighters: [...state.fighters, fighter] };
        }),

      removeFighter: (fighterId) =>
        set((state) => ({
          fighters: state.fighters.filter((f) => f.id !== fighterId),
        })),

      updateFighter: (fighterId, updates) =>
        set((state) => ({
          fighters: state.fighters.map((f) =>
            f.id === fighterId ? { ...f, ...updates } : f
          ),
        })),

      regenerateFightersHP: () => {
        const state = get();
        const now = Date.now();
        const diffMs = now - state.lastHPUpdate;
        const minutesPassed = Math.floor(diffMs / 60000);

        if (minutesPassed >= 1) {
          const newFighters = state.fighters.map(f => {
            const needsUpdate = f.health < f.maxHealth || f.stamina < f.maxStamina;
            if (needsUpdate) {
              return {
                ...f,
                health: Math.min(f.maxHealth, f.health + minutesPassed),
                stamina: f.maxStamina // Полностью восстанавливаем стамину
              };
            }
            return f;
          });

          set({
            fighters: newFighters,
            lastHPUpdate: now - (diffMs % 60000) // Сохраняем остаток времени
          });
        }
      },

      addWeaponToInventory: (weaponId, amount) =>
        set((state) => ({
          weaponInventory: {
            ...state.weaponInventory,
            [weaponId]: (state.weaponInventory[weaponId] || 0) + amount,
          },
        })),

      takeWeaponFromInventory: (weaponId, amount) => {
        const state = get();
        const currentCount = state.weaponInventory[weaponId] || 0;
        if (currentCount >= amount) {
          set({
            weaponInventory: {
              ...state.weaponInventory,
              [weaponId]: currentCount - amount,
            },
          });
          return true;
        }
        return false;
      },

      // Зелья
      addPotion: (type, amount) =>
        set((state) => ({
          potions: {
            greenPotion: state.potions?.greenPotion || 0,
            redPotion: state.potions?.redPotion || 0,
            [type]: (state.potions?.[type] || 0) + amount,
          },
        })),

      usePotion: (type) => {
        const state = get();
        const currentCount = state.potions?.[type] || 0;
        if (currentCount > 0) {
          set({
            potions: {
              greenPotion: state.potions?.greenPotion || 0,
              redPotion: state.potions?.redPotion || 0,
              [type]: currentCount - 1,
            },
          });
          return true;
        }
        return false;
      },

      updateAttackTimer: (seconds) => set({ attackTimer: seconds }),

      resetAttackTimer: () => {
        const state = get();
        const newTimer = getAttackTimer(state.liberation);
        set({
          attackTimer: newTimer,
          lastAttackTime: Date.now(),
        });
      },

      saveGame: () => {
        // Сохранение через persist middleware
      },

      loadGame: () => {
        // Загрузка через persist middleware
      },

      resetGame: () => {
        set({
          gold: 100,
          resources: {
            metal: 50,
            wood: 30,
            crystals: 10,
            leather: 10, // Начальное количество кожи
          },
          liberation: 0,
          currentBiome: 'plains',
          buildings: {
            barracks: false,
            forge: false,
            temple: false,
            mageTower: false,
          },
          fighters: [],
          maxFighters: 5,
          weaponInventory: {
            0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0
          },
          attackTimer: getAttackTimer(0),
          lastAttackTime: Date.now(),
          lastRaidTime: 0,
          lastHPUpdate: Date.now(),
          lastTaxCollectionTime: 0,
          language: 'en',
          hasSeenIntro: false,
          hasSeenBaseTutorial: false,
          hasSeenBattleTutorial: false,
          extendedTutorialStep: -1,
          extendedTutorialPhase: null,
          raidStats: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        });
      },
    }),
    {
      name: 'last-bastion-save',
      // Миграция только для обновления структуры данных, БЕЗ сброса прогресса
      migrate: (persistedState: any, _version: number) => {
        // Проверяем, запущена ли игра на itch.io и нужно ли сбросить сохранения
        const isItchIO = window.location.hostname.includes('itch.io') || 
                         window.location.href.includes('html5.gamedistribution.com');
        
        // Если это itch.io и есть сохранения - сбрасываем для чистого начала
        if (isItchIO && persistedState && !persistedState._itchCleared) {
          console.log('Detected itch.io environment - resetting game for new player');
          return null; // Вернем null для использования начальных значений
        }

        if (!persistedState) {
          // Если состояния нет - это первый запуск, возвращаем null для использования начальных значений
          return null;
        }

        // Миграция для старых сохранений без поля leather
        if (persistedState.resources) {
          // Если leather отсутствует или NaN, устанавливаем начальное значение
          if (persistedState.resources.leather === undefined || isNaN(persistedState.resources.leather)) {
            persistedState.resources.leather = 10;
          }
        }
        // Если hasSeenBattleTutorial отсутствует, устанавливаем false
        if (persistedState.hasSeenBattleTutorial === undefined) {
          persistedState.hasSeenBattleTutorial = false;
        }

        // Помечаем, что это itch.io версия (чтобы больше не сбрасывать)
        if (isItchIO && !persistedState._itchCleared) {
          persistedState._itchCleared = true;
        }

        // ВСЕГДА возвращаем сохраненное состояние - прогресс пользователя в приоритете!
        return persistedState;
      },
      version: 2,
    }
  )
);



